function cartDrawerSectionId() {
  const el = document.querySelector('cart-drawer');
  return (el && el.dataset.cdSection) || 'cart-drawer';
}

/* Returns a jQuery instance that actually has slick registered on it.

   Bare `$` cannot be trusted here: app scripts (Judge.me's review widget for
   one) load their own jQuery AFTER the theme's and overwrite window.$ — slick
   lives only on the theme's original instance, so `$(...).slick` throws
   "$(...).slick is not a function" on any page where an app got there first.
   The drawer usually opens well after apps have loaded, which is exactly when
   the clobbered `$` is all a bare reference would see.

   The first slick-capable instance found is pinned on window.__bbSlickJQ so a
   later clobbering of both globals cannot take it away mid-session. */
function cartDrawerJQ() {
  const pinned = window.__bbSlickJQ;
  if (pinned && pinned.fn && pinned.fn.slick) return pinned;
  const candidates = [window.jQuery, window.$];
  for (let i = 0; i < candidates.length; i++) {
    const jq = candidates[i];
    if (jq && jq.fn && jq.fn.slick) {
      window.__bbSlickJQ = jq;
      return jq;
    }
  }
  return null;
}
/* Pin eagerly at parse time too — this script is deferred, so the theme's
   jQuery + slick are already registered, while app scripts mostly are not. */
cartDrawerJQ();

/* Re-derives slidesToShow from the CURRENT viewport width and applies it.
   Needed because slick caches slidesToShow at init: `setPosition` alone
   recomputes pixel widths from the stale value, so a slider first measured at
   zero width stays stuck at one slide — and with slideCount <= slidesToShow
   slick marks both arrows slick-disabled, which is why they did nothing. */
function cartDrawerSyncSlides(wrap, track) {
  const jq = cartDrawerJQ();
  if (!jq) return;
  const $track = jq(track);
  if (!$track.hasClass('slick-initialized')) return;

  /* Bail on a zero-width measurement rather than committing slidesToShow: 1.
     open() runs in the same frame it adds .active, and a closed drawer measures
     zero — the transitionend handler syncs again once the panel is laid out. */
  const viewport = wrap.querySelector('.cd-ymal__viewport');
  if (viewport && viewport.getBoundingClientRect().width < 1) return;

  const next = cartDrawerSlidesToShow(wrap, track);
  if (next !== $track.slick('slickGetOption', 'slidesToShow')) {
    $track.slick('slickSetOption', 'slidesToShow', next, true);
  }
  $track.slick('setPosition');
}

function cartDrawerInitSlider(scope, attempt) {
  const wrap = (scope || document).querySelector('[data-cd-ymal]');
  if (!wrap) return;
  const track = wrap.querySelector('[data-cd-ymal-track]');
  if (!track || !track.children.length) return;

  const tries = attempt || 0;

  /* jquery.min.js and slick.min.js are both deferred, and the drawer can be
     opened (by an add-to-cart redirect) before they finish. Retry briefly
     instead of giving up silently and leaving a dead slider. */
  const jq = cartDrawerJQ();
  if (!jq) {
    if (tries < 60) requestAnimationFrame(() => cartDrawerInitSlider(scope, tries + 1));
    return;
  }

  if (jq(track).hasClass('slick-initialized')) {
    cartDrawerSyncSlides(wrap, track);
    return;
  }

  /* Don't init off a zero-width measurement. open() calls this in the same
     frame it adds .active, before the panel has been laid out at its real
     width, so wait for the viewport to actually have one. */
  const viewport = wrap.querySelector('.cd-ymal__viewport');
  if (viewport && viewport.getBoundingClientRect().width < 1) {
    if (tries < 60) requestAnimationFrame(() => cartDrawerInitSlider(scope, tries + 1));
    return;
  }

  const prev = wrap.querySelector('.cd-ymal__arrow--prev');
  const next = wrap.querySelector('.cd-ymal__arrow--next');

  jq(track).slick({
    slidesToShow: cartDrawerSlidesToShow(wrap, track),
    slidesToScroll: 1,
    infinite: false,
    dots: false,
    speed: 400,
    cssEase: 'ease',
    arrows: !!(prev && next),
    appendArrows: wrap.querySelector('[data-cd-ymal-arrows]'),
    prevArrow: prev || undefined,
    nextArrow: next || undefined,
  });
}

function cartDrawerSlidesToShow(wrap, track) {
  const viewport = wrap.querySelector('.cd-ymal__viewport');
  const available = viewport ? viewport.getBoundingClientRect().width : 0;
  const gap = parseFloat(getComputedStyle(track).getPropertyValue('--cd-ymal-gap')) || 0;
  const isMobile = window.matchMedia('(max-width: 749px)').matches;
  const card = parseFloat(isMobile ? wrap.dataset.cdYmalWidthM : wrap.dataset.cdYmalWidth) || 0;

  if (!available || !card) return 1;

  const show = Math.round((available / (card + gap)) * 100) / 100;
  return Math.min(Math.max(show, 1), track.children.length);
}

function cartDrawerLoadRecommendations(scope) {
  const wrap = (scope || document).querySelector('[data-cd-ymal]');
  if (!wrap) return;

  const track = wrap.querySelector('[data-cd-ymal-track]');
  if (!track) return;

  if (track.children.length) {
    wrap.removeAttribute('hidden');
    cartDrawerInitSlider(scope);
    return;
  }

  if (wrap.dataset.cdYmalMode !== 'recommend') return;

  const url = wrap.dataset.cdYmalUrl;
  if (!url || /product_id=(&|$)/.test(url)) return;

  /* Fetch at most once per wrap element. cart.js swaps .cd-ymal out with
     replaceWith on every cart update, so the flag resets naturally for the new
     cart contents — while a response that comes back empty can't put the
     observer below into a refetch loop. */
  if (wrap.dataset.cdYmalFetched === '1') return;
  wrap.dataset.cdYmalFetched = '1';

  fetch(url)
    .then((response) => response.text())
    .then((text) => {
      const source = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-cd-ymal-track]');
      if (!source || !source.innerHTML.trim()) return;
      track.innerHTML = source.innerHTML;
      wrap.removeAttribute('hidden');
      cartDrawerInitSlider(scope);
    })
    .catch(() => {});
}

let cartDrawerResizeTimer;
window.addEventListener('resize', () => {
  clearTimeout(cartDrawerResizeTimer);
  cartDrawerResizeTimer = setTimeout(() => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || !drawer.classList.contains('active')) return;
    const wrap = drawer.querySelector('[data-cd-ymal]');
    const track = wrap && wrap.querySelector('[data-cd-ymal-track]');
    if (!track || !cartDrawerJQ()) return;
    cartDrawerSyncSlides(wrap, track);
  }, 200);
});

function cartDrawerAnimateProgress(scope) {
  const drawer = document.querySelector('cart-drawer');
  const fill = (scope || document).querySelector('.cd-ship__fill');
  if (!fill) {
    if (drawer) delete drawer.dataset.cdProgress;
    return;
  }

  const target = fill.style.width;
  if (!target) return;

  const previous = drawer ? drawer.dataset.cdProgress : undefined;
  if (drawer) drawer.dataset.cdProgress = target;

  if (previous === undefined || previous === target) return;

  fill.style.width = previous;
  void fill.offsetWidth;
  fill.style.width = target;
}

/* Idempotent: brings the slider to a correct state whatever it is currently in —
   missing slides, present-but-unslicked, or already live but measured wrong.
   Safe to call as often as you like. */
function cartDrawerEnsureSlider(scope) {
  const root = scope && scope.querySelector ? scope : document;
  const wrap = root.querySelector('[data-cd-ymal]');
  if (!wrap) return;
  const track = wrap.querySelector('[data-cd-ymal-track]');
  if (!track) return;

  if (!track.children.length) {
    cartDrawerLoadRecommendations(scope);
    return;
  }

  wrap.removeAttribute('hidden');
  cartDrawerInitSlider(scope);
}

/* The drawer is re-rendered three different ways — innerHTML in
   CartDrawer.renderContents, replaceWith on .cd-ymal in cart.js's onCartUpdate,
   and the recommendations fetch below — each of which drops a fresh, unslicked
   track into the page. Rather than hooking every one of those call sites (and
   any added later), watch the <cart-drawer> element itself: it is a custom
   element that is never replaced, only its contents are.

   childList only, deliberately. slick's init rewrites the track's children,
   which re-triggers this, but cartDrawerEnsureSlider is idempotent and the
   second pass only recalculates geometry — no childList change, so it settles. */
function cartDrawerObserveSlider() {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer || drawer.dataset.cdYmalObserved === '1') return;
  drawer.dataset.cdYmalObserved = '1';

  let timer;
  new MutationObserver(() => {
    clearTimeout(timer);
    timer = setTimeout(() => cartDrawerEnsureSlider(drawer), 80);
  }).observe(drawer, { childList: true, subtree: true });
}

function cartDrawerAfterRender(scope) {
  cartDrawerAnimateProgress(scope);
  cartDrawerObserveSlider();
  cartDrawerEnsureSlider(scope);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', cartDrawerObserveSlider);
} else {
  cartDrawerObserveSlider();
}

function cartDrawerRefresh() {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return Promise.resolve();

  return fetch(`${window.location.pathname}?section_id=${cartDrawerSectionId()}`)
    .then((response) => response.text())
    .then((text) => {
      const parsed = new DOMParser().parseFromString(text, 'text/html');
      const source = parsed.querySelector('.cd-panel');
      const target = drawer.querySelector('.cd-panel');
      if (source && target) target.innerHTML = source.innerHTML;
      drawer.classList.toggle('is-empty', !!drawer.querySelector('.cd-empty'));
      return fetch(`${window.location.pathname}?section_id=cart-icon-bubble`);
    })
    .then((response) => response.text())
    .then((text) => {
      const bubbleSource = new DOMParser().parseFromString(text, 'text/html').querySelector('.cart-count-bubble');
      const bubbleTarget = document.querySelector('#cart-icon-bubble .cart-count-bubble');
      if (bubbleSource && bubbleTarget) bubbleTarget.innerHTML = bubbleSource.innerHTML;
      cartDrawerAfterRender(drawer);
    })
    .catch(() => {});
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();

    const iconsearch = document.querySelector('.header__search');
    if (iconsearch)
      iconsearch.addEventListener('click', () => {
        this.classList.contains('active') && this.close();
      });

    const iconaccount = document.querySelector('.header__icon--account');
    if (iconaccount)
      iconaccount.addEventListener('click', () => {
        this.classList.contains('active') && this.close();
      });

    this.stickyHeaderAvailable = document.querySelector('sticky-header') ? true : false;
  }

  connectedCallback() {
    let stickyHeaderHeight = 0;
    document.querySelectorAll('.section-header').forEach((ele) => {
      stickyHeaderHeight += ele.getBoundingClientRect().height;
    });
    let headerHeight = stickyHeaderHeight;
    document.querySelectorAll('.utility-bar').forEach((ele) => {
      headerHeight += ele.getBoundingClientRect().height;
    });
    document.documentElement.style.setProperty('--header-announce-height', headerHeight + 'px');
    document.documentElement.style.setProperty('--header-sticky-height', stickyHeaderHeight + 'px');
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.classList.contains('active') ? this.close() : this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });

    if (cartLink.hasAttribute('data-cd-pending')) {
      cartLink.removeAttribute('data-cd-pending');
      this.open(cartLink);
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
      cartDrawerAfterRender(this);
    });

    /* transitionend bubbles from descendants, so `once: true` could be consumed
       by a child's transition firing first — leaving the panel's own slide-in
       unhandled and the slider measured mid-animation. Filter to the panel and
       unbind by hand once it has actually been handled. */
    const onOpenTransitionEnd = (event) => {
      if (event.target !== this && !event.target.classList.contains('cd-panel')) return;
      this.removeEventListener('transitionend', onOpenTransitionEnd);

      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty') || document.getElementById('CartDrawer')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.cd-panel') || this.querySelector('.cd-head__close');
      if (containerToTrapFocusOn) trapFocus(containerToTrapFocusOn, focusElement);
      cartDrawerInitSlider(this);
    };
    this.addEventListener('transitionend', onOpenTransitionEnd);

    document.body.classList.add('overflow-hidden-drawer');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden-drawer');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = document.querySelector(section.target || section.selector || `#${section.id}`);
      if (!sectionElement || !parsedState.sections[section.id]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.classList.toggle('is-empty', !!this.querySelector('.cd-empty'));

    setTimeout(() => {
      this.bindOverlay();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return node ? node.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: cartDrawerSectionId(),
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        selector: '.cart-count-bubble',
        target: '#cart-icon-bubble .cart-count-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: cartDrawerSectionId(),
        selector: '.cd-panel',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.cart-count-bubble',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

const cartDrawerMarkLoading = (target, kind) => {
  const item = target && target.closest ? target.closest('.cd-item') : null;
  if (item) item.setAttribute('data-cd-loading', kind);
};

document.addEventListener(
  'click',
  (event) => {
    if (event.target.closest('cart-remove-button')) cartDrawerMarkLoading(event.target, 'remove');
    else if (event.target.closest('.cd-qty__btn')) cartDrawerMarkLoading(event.target, 'qty');
  },
  true
);

document.addEventListener(
  'change',
  (event) => {
    if (event.target.closest('.cd-qty__input')) cartDrawerMarkLoading(event.target, 'qty');
  },
  true
);

document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-cd-details]');
  if (!toggle) return;
  event.preventDefault();
  const isOpen = toggle.getAttribute('aria-expanded') === 'true';
  toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
  const panel = document.getElementById(toggle.getAttribute('aria-controls'));
  if (panel) panel.classList.toggle('is-closed', isOpen);
});

function cartDrawerShopRoot() {
  return window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
}

function cartDrawerAppliedCodes() {
  return Array.from(document.querySelectorAll('[data-cd-promo-code]')).map((pill) => pill.dataset.cdPromoCode);
}

function cartDrawerSetMessage(node, text, state) {
  if (!node) return;
  node.textContent = text || '';
  node.classList.remove('is-error', 'is-success');
  if (state) node.classList.add(state);
}

function cartDrawerWriteDiscounts(codes) {
  const url = window.routes && window.routes.cart_update_url ? window.routes.cart_update_url : `${cartDrawerShopRoot()}cart/update.js`;
  return fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ discount: codes.join(',') }),
  }).then((response) => response.json());
}

function cartDrawerHasCode(cart, code) {
  return (cart && cart.discount_codes ? cart.discount_codes : []).some(
    (entry) => String(entry.code).toLowerCase() === String(code).toLowerCase() && entry.applicable !== false
  );
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-cd-promo-apply]');
  if (!button || button.classList.contains('is-loading')) return;
  event.preventDefault();

  const box = button.closest('[data-cd-promo]');
  if (!box) return;

  const input = box.querySelector('[data-cd-promo-input]');
  const message = box.querySelector('[data-cd-promo-msg]');
  const code = input ? input.value.trim() : '';
  const successText = button.dataset.cdPromoSuccess || '';
  const errorText = button.dataset.cdPromoError || '';
  const emptyText = button.dataset.cdPromoEmpty || '';

  cartDrawerSetMessage(message, '', null);

  if (!code) {
    cartDrawerSetMessage(message, emptyText, 'is-error');
    if (input) {
      input.classList.add('is-invalid');
      input.focus();
      input.addEventListener('input', () => input.classList.remove('is-invalid'), { once: true });
    }
    return;
  }
  if (input) input.classList.remove('is-invalid');

  const spinner = button.querySelector('.cd-spin--btn');
  const stopLoading = () => {
    button.classList.remove('is-loading');
    if (spinner) spinner.classList.add('hidden');
  };

  button.classList.add('is-loading');
  if (spinner) spinner.classList.remove('hidden');

  const existing = cartDrawerAppliedCodes().filter((entry) => entry.toLowerCase() !== code.toLowerCase());

  cartDrawerWriteDiscounts(existing.concat([code]))
    .then((cart) => {
      if (cartDrawerHasCode(cart, code)) return cart;
      return fetch(`${cartDrawerShopRoot()}discount/${encodeURIComponent(code)}`, {
        method: 'GET',
        credentials: 'same-origin',
      })
        .then(() => fetch(`${cartDrawerShopRoot()}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } }))
        .then((response) => response.json());
    })
    .then((cart) => {
      if (!cartDrawerHasCode(cart, code)) {
        cartDrawerSetMessage(message, errorText, 'is-error');
        stopLoading();
        return;
      }

      return cartDrawerRefresh().then(() => {
        const freshBox = document.querySelector('[data-cd-promo]');
        cartDrawerSetMessage(freshBox && freshBox.querySelector('[data-cd-promo-msg]'), successText, 'is-success');
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-discount', cartData: cart });
        }
      });
    })
    .catch(() => {
      cartDrawerSetMessage(message, errorText, 'is-error');
      stopLoading();
    });
});

document.addEventListener('click', (event) => {
  const remove = event.target.closest('[data-cd-promo-remove]');
  if (!remove) return;
  event.preventDefault();

  const pill = remove.closest('[data-cd-promo-code]');
  const list = remove.closest('[data-cd-promo-list]');
  if (!pill || pill.classList.contains('is-loading')) return;

  const code = pill.dataset.cdPromoCode;
  const spinner = pill.querySelector('.cd-spin--pill');
  const message = document.querySelector('[data-cd-promo-msg]');
  const errorText = document.querySelector('[data-cd-promo-apply]');

  pill.classList.add('is-loading');
  if (spinner) spinner.classList.remove('hidden');
  if (list) list.classList.add('is-busy');

  cartDrawerSetMessage(message, '', null);

  const remaining = cartDrawerAppliedCodes().filter((entry) => entry.toLowerCase() !== code.toLowerCase());

  cartDrawerWriteDiscounts(remaining)
    .then((cart) => {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-discount', cartData: cart });
      }
      return cartDrawerRefresh();
    })
    .catch(() => {
      pill.classList.remove('is-loading');
      if (spinner) spinner.classList.add('hidden');
      if (list) list.classList.remove('is-busy');
      cartDrawerSetMessage(
        document.querySelector('[data-cd-promo-msg]'),
        errorText ? errorText.dataset.cdPromoError : '',
        'is-error'
      );
    });
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter') return;
  const input = event.target.closest('[data-cd-promo-input]');
  if (!input) return;
  event.preventDefault();
  const apply = input.closest('[data-cd-promo]').querySelector('[data-cd-promo-apply]');
  if (apply) apply.click();
});

if (window.Shopify && window.Shopify.designMode) {
  const editorDrawer = (event) => (event.target ? event.target.querySelector('cart-drawer') : null);

  document.addEventListener('shopify:section:load', (event) => {
    const drawer = editorDrawer(event);
    if (drawer) cartDrawerAfterRender(drawer);
  });

  document.addEventListener('shopify:section:select', (event) => {
    const drawer = editorDrawer(event);
    if (drawer) drawer.open();
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    const drawer = editorDrawer(event);
    if (drawer) drawer.close();
  });

  document.addEventListener('shopify:block:select', (event) => {
    const drawer = event.target.closest('.shopify-section')
      ? event.target.closest('.shopify-section').querySelector('cart-drawer')
      : null;
    if (drawer) drawer.open();
  });
}
